import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { GET_PRODUCTION_DECLARATION_MATERIALS_KEY } from '@/keys';
import { useDeleteProductionDeclarationCorrectionRequest } from '@/services/apis/mutations/production-declaration/use-delete-production-declaration-correction-request';
import type { CorrectionRequest } from '@/types/core/purchase-order/correction-request';

type UseProductionDeclarationCorrectionRequestCommentDeleteParams = {
  commentsQueryKey: QueryKey;
  commodityOriginId: string;
  materialId: string;
  productionDeclarationId: string;
};

type ApiErrorResponse = {
  detail?: string;
  message?: string;
};

export function useProductionDeclarationCorrectionRequestCommentDelete({
  commentsQueryKey,
  commodityOriginId,
  materialId,
  productionDeclarationId,
}: UseProductionDeclarationCorrectionRequestCommentDeleteParams) {
  const [selectedComment, setSelectedComment] =
    useState<CorrectionRequest | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const { t: tCommon } = useTranslation('common', {
    useSuspense: false,
  });
  const { isPending: isDeletingComment, mutate: deleteCorrectionRequest } =
    useDeleteProductionDeclarationCorrectionRequest();
  const handleDeleteClick = (comment: CorrectionRequest) => {
    setSelectedComment(comment);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    if (isDeletingComment) {
      return;
    }
    setSelectedComment(null);
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteDialogOpenChange = (open: boolean) => {
    if (isDeletingComment) {
      return;
    }
    if (!open) {
      handleDeleteCancel();
      return;
    }
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedComment === null) {
      return;
    }

    deleteCorrectionRequest(
      {
        productionDeclarationId,
        materialId,
        commodityOriginId,
        correctionRequestId: selectedComment.id,
      },
      {
        onSuccess: async () => {
          queryClient.setQueryData<CorrectionRequest[]>(
            commentsQueryKey,
            (currentComments = []) =>
              currentComments.filter(
                (currentComment) => currentComment.id !== selectedComment.id,
              ),
          );
          await queryClient.invalidateQueries({
            queryKey: [
              ...GET_PRODUCTION_DECLARATION_MATERIALS_KEY,
              productionDeclarationId,
            ],
          });
          setSelectedComment(null);
          setIsDeleteDialogOpen(false);
          toast.success(t('materialCommodityOrigins.comments.deleteSuccess'));
        },
        onError: (error) => {
          const description = axios.isAxiosError<ApiErrorResponse>(error)
            ? (error.response?.data?.message ??
              error.response?.data?.detail ??
              t('materialCommodityOrigins.comments.deleteError'))
            : t('materialCommodityOrigins.comments.deleteError');

          toast.error(tCommon('error'), {
            description,
          });
        },
      },
    );
  };

  return {
    handleDeleteCancel,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteDialogOpenChange,
    isDeleteDialogOpen,
    isDeletingComment,
  };
}
